/** Lightweight legal copy for CrafterHack auth & product surfaces. */

export const LEGAL_DOCS = {
  terms: {
    id: 'terms',
    title: 'Terms of Service',
    updated: 'July 26, 2026',
    sections: [
      {
        heading: '1. Agreement',
        body: `By creating an account or using CrafterHack (“the Platform”, “we”, “us”), you agree to these Terms of Service. If you do not agree, do not use the Platform.`,
      },
      {
        heading: '2. Who we are',
        body: `CrafterHack is a learning and interview-prep platform offering coding problems, contests, practice tracks, live coding rooms, mock interviews, and related educational content.`,
      },
      {
        heading: '3. Accounts',
        body: `You must provide accurate registration information and keep your credentials secure. You are responsible for activity under your account. Notify us promptly if you suspect unauthorized access. We may suspend or terminate accounts that violate these terms or harm other users.`,
      },
      {
        heading: '4. Acceptable use',
        body: `You agree not to: (a) cheat in contests or share solutions in ways that undermine fair play; (b) harass others in live rooms or elsewhere; (c) attempt to disrupt, scrape excessively, reverse engineer, or gain unauthorized access to our systems; (d) upload malware or abusive content; (e) use the Platform for unlawful purposes. We may remove content or restrict access at our discretion.`,
      },
      {
        heading: '5. Content & code',
        body: `Problems, editorial content, and Platform UI are owned by CrafterHack or its licensors. Code you write in the editor remains yours. By submitting solutions, you grant us a non-exclusive license to store, run, and display them as needed to operate features (judge, profile, contests). Do not submit code you do not have rights to use.`,
      },
      {
        heading: '6. Paid services',
        body: `Some features (for example mock interviews or courses) may require payment. Prices, taxes, and refund rules are shown at checkout. Unless stated otherwise, digital services are non-refundable once delivered or the session window has begun. Failed or disputed payments may result in revoked access.`,
      },
      {
        heading: '7. Live coding & third parties',
        body: `Live rooms use real-time media and collaboration. You are responsible for what you share on camera or screen. Google Sign-In and payment providers are governed by their own terms. We are not responsible for outages of third-party services.`,
      },
      {
        heading: '8. Disclaimers',
        body: `The Platform is provided “as is” for educational purposes. We do not guarantee interview outcomes, job offers, uninterrupted availability, or that judge results are error-free. To the fullest extent permitted by law, CrafterHack is not liable for indirect, incidental, or consequential damages arising from your use of the Platform.`,
      },
      {
        heading: '9. Changes',
        body: `We may update these Terms. Continued use after changes take effect constitutes acceptance. Material changes may be highlighted in-product or by email when practical.`,
      },
      {
        heading: '10. Contact',
        body: `Questions about these Terms: updates@crafterhack.com`,
      },
    ],
  },
  privacy: {
    id: 'privacy',
    title: 'Privacy Policy',
    updated: 'July 26, 2026',
    sections: [
      {
        heading: '1. Overview',
        body: `This Privacy Policy explains what we collect, how we use it, and the choices you have when using CrafterHack.`,
      },
      {
        heading: '2. Information we collect',
        body: `Account data: name, email, username, and password hash (or Google account identifiers if you use Google Sign-In). Profile & activity: solved problems, contest results, points, preferences (e.g. theme), and code submissions needed to run and score your work. Technical data: IP address, device/browser type, and basic usage logs for security and reliability. Payments: processed by our payment provider; we receive confirmation and limited billing metadata, not full card numbers. Communications: messages you send us (feedback, support).`,
      },
      {
        heading: '3. How we use information',
        body: `We use data to: provide and improve the Platform; authenticate you; run contests and judging; personalize practice; send transactional email (password reset, challenge reminders, booking confirmations); prevent abuse; and comply with law. We do not sell your personal information.`,
      },
      {
        heading: '4. Sharing',
        body: `We may share data with: infrastructure and email providers who process it on our behalf; Google (if you choose Google Sign-In); payment processors for paid features; and authorities when legally required. Public profile elements (username, points, solved counts) may be visible to other users as part of leaderboards and profiles.`,
      },
      {
        heading: '5. Cookies & local storage',
        body: `We use local storage / cookies for session tokens, theme preference, and similar product settings. You can clear these in your browser; doing so may sign you out or reset preferences.`,
      },
      {
        heading: '6. Retention',
        body: `We keep account and submission data while your account is active and as needed for contests, security, and legal obligations. You may request account deletion by contacting us; some anonymized or aggregated records may remain.`,
      },
      {
        heading: '7. Security',
        body: `We use industry-standard practices such as hashed passwords and HTTPS. No method of transmission or storage is 100% secure—please use a strong unique password and enable Google Sign-In if preferred.`,
      },
      {
        heading: '8. Children',
        body: `CrafterHack is intended for users who can form a binding contract in their jurisdiction (generally 18+, or with guardian consent where applicable). We do not knowingly collect data from children under 13.`,
      },
      {
        heading: '9. Your choices',
        body: `You can update profile details in-product, reset your password, or contact us to request access, correction, or deletion of personal data where applicable law provides those rights.`,
      },
      {
        heading: '10. Contact',
        body: `Privacy questions: updates@crafterhack.com`,
      },
    ],
  },
  dsaToolkitPrivacy: {
    id: 'dsaToolkitPrivacy',
    title: 'DSA Toolkit — Privacy Policy',
    updated: 'August 11, 2026',
    sections: [
      {
        heading: 'Summary',
        body: `DSA Toolkit does not collect, transmit, sell, or share any of your data. It has no backend server, no analytics, and no third-party network calls of any kind. Everything it stores stays on your device, or in your own Google/Microsoft account sync for the small settings it uses chrome.storage.sync for.`,
      },
      {
        heading: 'What the extension can access, and why',
        body: `Saved boilerplate templates are stored locally (chrome.storage.local) so the extension can insert them into the editor when you trigger it — never transmitted anywhere. Settings (insert mode, default language, theme, shortcut on/off) are stored via chrome.storage.sync so they persist across your signed-in browsers; DSA Toolkit never sends this data anywhere itself. The active tab's URL and page content are read only on supported judge sites, only to detect the language selector and insert your template into that page's editor. The keyboard shortcut trigger is used only to know when to run the insert action.`,
      },
      {
        heading: 'Host permissions',
        body: `By default, host permissions are limited to the judge sites the extension explicitly supports (LeetCode, Codeforces, GeeksforGeeks, HackerRank, CodeChef, AtCoder, Coding Ninjas). If you use Insert on a site outside that list, the extension asks your browser — not us — for permission on that one domain, one time, before doing anything there. Nothing is requested silently, at install time, or for any site you haven't actually tried to use it on. You can review or revoke any granted site at any time from your browser's extension settings.`,
      },
      {
        heading: 'No account, no tracking',
        body: `DSA Toolkit does not require an account, does not use cookies for tracking, and does not include any advertising or analytics SDKs.`,
      },
      {
        heading: 'Future paid features',
        body: `If DSA Toolkit introduces optional paid features later, any data handling required for that (for example, purchase or license verification) will be disclosed here and in the store listing before it ships, and will never include your saved templates or code.`,
      },
      {
        heading: 'Changes to this policy',
        body: `If this policy changes, the date above will change, and material changes will be called out in the extension's release notes.`,
      },
      {
        heading: 'Contact',
        body: `Questions about this policy: updates@crafterhack.com`,
      },
    ],
  },
};
